// ============================================
// VOLUNTEER MODULE - REPORTS PAGE
// Click any category → opens filter panel
// Each category has rich field-based filters
// + date range + sort + Excel download
// ============================================

import React, { useState, useEffect, useMemo } from "react";
import { useTheme } from "../components/ThemeContext";
import {
  getEvents, getVenues, getVolunteers, downloadReport,
  downloadContextualReport, API,
} from "../services/api";

const Msg = ({ msg, C }) => !msg ? null : (
  <div style={{ padding:"10px 14px", borderRadius:9, marginBottom:12,
    background:msg.type==="success"?C.greenSoft:C.redSoft,
    border:`1px solid ${msg.type==="success"?C.green:C.red}44`,
    color:msg.type==="success"?C.green:C.red, fontSize:13, fontWeight:600 }}>
    {msg.text}
  </div>
);

// ── Preview table ─────────────────────────
function PreviewTable({ rows, C }) {
  if(!rows||!rows.length) return (
    <div style={{ padding:"14px 0", fontSize:12, color:C.muted, fontStyle:"italic" }}>
      No data matches the current filters.
    </div>
  );
  const cols = Object.keys(rows[0]);
  return (
    <div style={{ overflowX:"auto", marginBottom:12, border:`1px solid ${C.border}`, borderRadius:9, fontSize:11 }}>
      <table style={{ width:"100%", borderCollapse:"collapse" }}>
        <thead>
          <tr style={{ background:C.bg }}>
            {cols.map(c=>(
              <th key={c} style={{ padding:"7px 10px", textAlign:"left", color:C.muted, fontWeight:700, textTransform:"uppercase", fontSize:9, letterSpacing:"0.5px", borderBottom:`1px solid ${C.border}`, whiteSpace:"nowrap" }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0,8).map((row,i)=>(
            <tr key={i} style={{ borderBottom:`1px solid ${C.border}` }}>
              {cols.map(c=>(
                <td key={c} style={{ padding:"6px 10px", color:C.text, whiteSpace:"nowrap" }}>{String(row[c]||"—")}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
// CATEGORY CARD — clickable card → opens filter panel
// ============================================================
function CategoryCard({ icon, label, description, color, count, onClick, active, C }) {
  return (
    <button onClick={onClick}
      style={{
        textAlign:"left", padding:"18px 20px",
        background: active ? `${color}15` : C.card,
        border: `2px solid ${active ? color : C.border}`,
        borderRadius:14, cursor:"pointer", transition:"all 0.2s",
        display:"flex", flexDirection:"column", gap:8,
        position:"relative",
      }}
      onMouseEnter={e => !active && (e.currentTarget.style.borderColor = color+"66")}
      onMouseLeave={e => !active && (e.currentTarget.style.borderColor = C.border)}>
      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
        <div style={{ width:42, height:42, borderRadius:11, background:`${color}22`, border:`1px solid ${color}44`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>
          {icon}
        </div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:14, fontWeight:800, color:C.text }}>{label}</div>
          {count !== undefined && (
            <div style={{ fontSize:11, color, fontWeight:700, marginTop:2 }}>{count} records</div>
          )}
        </div>
        {active && <span style={{ fontSize:18, color }}>▼</span>}
      </div>
      <div style={{ fontSize:12, color:C.muted, lineHeight:1.5 }}>{description}</div>
    </button>
  );
}

// ============================================================
// MAIN REPORTS PAGE
// ============================================================
export default function Reports() {
  const { C } = useTheme();
  const [events, setEvents]       = useState([]);
  const [venues, setVenues]       = useState([]);
  const [volunteers, setVols]     = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [msg, setMsg]             = useState(null);

  useEffect(() => {
    Promise.all([getEvents(), getVenues(), getVolunteers()])
      .then(([e, v, vol])=>{
        setEvents(e.data.events||[]);
        setVenues(v.data.venues||[]);
        setVols(vol.data.volunteers||[]);
      }).catch(()=>{});
  }, []);

  const flash = (m) => { setMsg(m); setTimeout(()=>setMsg(null),3000); };

  // ── Categories — each maps to a different report builder ─
  const CATEGORIES = [
    { id:"volunteers",  icon:"👥", label:"Volunteer Reports",
      description:"Who is registered, where they're from, when they arrive — filter by event, Jamiat, status, dates and more",
      color:C.accent, count:volunteers.length },
    { id:"events",      icon:"📅", label:"Event Reports",
      description:"Events filtered by status, type, date range — full lifecycle view",
      color:C.green, count:events.length },
    { id:"assignments", icon:"🗂️", label:"Duty Assignment Reports",
      description:"Who is assigned to which gate, when — by event, venue, gate or date",
      color:C.orange, count:null },
    { id:"attendance",  icon:"✅", label:"Attendance Reports",
      description:"Present, absent, late records by event, date or volunteer",
      color:C.purple, count:null },
    { id:"venues",      icon:"🏟️", label:"Venue & Gate Reports",
      description:"Venues, gates, capacity utilisation and fill rates",
      color:C.red, count:venues.length },
    { id:"messaging",   icon:"💬", label:"Messaging Log Reports",
      description:"WhatsApp and SMS message history by date, volunteer or status",
      color:C.green, count:null },
  ];

  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <div style={{ fontSize:22, fontWeight:800, color:C.text, fontFamily:"Georgia, serif" }}>Reports</div>
        <div style={{ fontSize:13, color:C.muted, marginTop:4 }}>
          Click a category below, choose your filters, preview results, then download Excel.
        </div>
      </div>

      <Msg msg={msg} C={C} />

      {/* ── FULL SUMMARY — featured shortcut ─ */}
      <div style={{ background:`linear-gradient(135deg,${C.accent}18,${C.purple}11)`, border:`2px solid ${C.accent}44`, borderRadius:16, padding:20, marginBottom:24, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:14 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <span style={{ fontSize:28 }}>📊</span>
          <div>
            <div style={{ fontSize:15, fontWeight:800, color:C.text }}>Full Event Summary</div>
            <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>One-click download — all volunteers, events, venues, assignments and attendance in one Excel file with multiple sheets.</div>
          </div>
        </div>
        <button onClick={()=>{ downloadReport("full_summary"); flash({type:"success",text:"Download started!"}); }}
          style={{ padding:"12px 22px", background:C.accent, border:"none", borderRadius:10, color:"#000", fontSize:13, fontWeight:800, cursor:"pointer", whiteSpace:"nowrap" }}>
          📥 Download Full Report
        </button>
      </div>

      {/* ── CATEGORY GRID ─ */}
      <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:12 }}>Choose a Report Category</div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px,1fr))", gap:12, marginBottom:20 }}>
        {CATEGORIES.map(cat=>(
          <CategoryCard key={cat.id} {...cat}
            active={activeCategory===cat.id}
            onClick={()=>setActiveCategory(activeCategory===cat.id?null:cat.id)}
            C={C} />
        ))}
      </div>

      {/* ── ACTIVE CATEGORY PANEL ─ */}
      {activeCategory && (
        <div style={{ background:C.card, border:`2px solid ${CATEGORIES.find(c=>c.id===activeCategory).color}`, borderRadius:14, padding:22, marginBottom:20 }}>
          {activeCategory==="volunteers"  && <VolunteerBuilder events={events} volunteers={volunteers} C={C} flash={flash} />}
          {activeCategory==="events"      && <EventBuilder events={events} C={C} flash={flash} />}
          {activeCategory==="assignments" && <AssignmentBuilder events={events} venues={venues} C={C} flash={flash} />}
          {activeCategory==="attendance"  && <AttendanceBuilder events={events} C={C} flash={flash} />}
          {activeCategory==="venues"      && <VenueBuilder events={events} C={C} flash={flash} />}
          {activeCategory==="messaging"   && <MessagingBuilder C={C} flash={flash} />}
        </div>
      )}

      <div style={{ marginTop:20, padding:"12px 16px", background:C.card, border:`1px solid ${C.border}`, borderRadius:12, fontSize:12, color:C.muted }}>
        📌 All reports download as <strong style={{color:C.text}}>.xlsx Excel files</strong> reflecting the current state of the system.
      </div>
    </div>
  );
}

// ============================================================
// SHARED FILTER PANEL HELPERS
// ============================================================
const inpStyle = (C) => ({
  padding:"9px 12px", background:C.inputBg, border:`1px solid ${C.border}`,
  borderRadius:9, color:C.text, fontSize:13, outline:"none", width:"100%", boxSizing:"border-box",
});

function FilterField({ label, children, C }) {
  return (
    <div>
      <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>{label}</div>
      {children}
    </div>
  );
}

function ActionRow({ canDownload, onPreview, onDownload, loading, C }) {
  return (
    <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginTop:6 }}>
      <button onClick={onPreview} disabled={loading||!canDownload}
        style={{ padding:"10px 20px", background:canDownload?C.accentSoft:C.border, border:`1px solid ${canDownload?C.accent:C.border}`, borderRadius:10, color:canDownload?C.accent:C.muted, fontSize:13, fontWeight:600, cursor:canDownload?"pointer":"not-allowed" }}>
        {loading?"Loading...":"Preview Data"}
      </button>
      <button onClick={onDownload} disabled={!canDownload}
        style={{ padding:"10px 22px", background:canDownload?C.accent:C.border, border:"none", borderRadius:10, color:canDownload?"#000":C.muted, fontSize:13, fontWeight:700, cursor:canDownload?"pointer":"not-allowed" }}>
        📥 Download Excel
      </button>
    </div>
  );
}

// ============================================================
// VOLUNTEER REPORT BUILDER
// Filter by: event, jamiat, jamaat, designation, zone,
//            status, arrival range, departure range
// ============================================================
function VolunteerBuilder({ events, volunteers, C, flash }) {
  const [eventId, setEventId]       = useState("");
  const [jamiat, setJamiat]         = useState("");
  const [jamaat, setJamaat]         = useState("");
  const [designation, setDesig]     = useState("");
  const [zone, setZone]             = useState("");
  const [status, setStatus]         = useState("");
  const [arrFrom, setArrFrom]       = useState("");
  const [arrTo, setArrTo]           = useState("");
  const [depFrom, setDepFrom]       = useState("");
  const [depTo, setDepTo]           = useState("");
  const [sort, setSort]             = useState("full_name");
  const [dir, setDir]               = useState("asc");
  const [preview, setPreview]       = useState(null);
  const [loading, setLoading]       = useState(false);

  const designations = useMemo(() => [...new Set(volunteers.map(v=>v.designation).filter(Boolean))].sort(), [volunteers]);
  const jamiats      = useMemo(() => [...new Set(volunteers.map(v=>v.jamiat).filter(Boolean))].sort(),      [volunteers]);

  // Build filter — applied client-side for live preview
  const buildFilter = (vols) => vols.filter(v=>{
    if(eventId     && v.eventId         !== eventId)  return false;
    if(jamiat      && !(v.jamiat||"").toLowerCase().includes(jamiat.toLowerCase()))     return false;
    if(jamaat      && !(v.jamaat||"").toLowerCase().includes(jamaat.toLowerCase()))     return false;
    if(designation && v.designation     !== designation) return false;
    if(zone        && !(v.zone||"").toLowerCase().includes(zone.toLowerCase()))         return false;
    if(status      && v.registrationStatus !== status) return false;
    if(arrFrom     && v.arrivalDate     < arrFrom)    return false;
    if(arrTo       && v.arrivalDate     > arrTo)      return false;
    if(depFrom     && v.departureDate   < depFrom)    return false;
    if(depTo       && v.departureDate   > depTo)      return false;
    return true;
  });

  const handlePreview = () => {
    setLoading(true);
    const filtered = buildFilter(volunteers);
    const sorted = [...filtered].sort((a,b)=>{
      const va=String(a[sort==="full_name"?"fullName":sort==="its_id"?"itsId":sort==="arrival_date"?"arrivalDate":sort==="departure_date"?"departureDate":sort==="registration_status"?"registrationStatus":sort]||"").toLowerCase();
      const vb=String(b[sort==="full_name"?"fullName":sort==="its_id"?"itsId":sort==="arrival_date"?"arrivalDate":sort==="departure_date"?"departureDate":sort==="registration_status"?"registrationStatus":sort]||"").toLowerCase();
      return dir==="asc"?va.localeCompare(vb):vb.localeCompare(va);
    });
    setPreview({
      total: sorted.length,
      rows:  sorted.slice(0,8).map(v=>({
        "ITS ID":v.itsId, "Name":v.fullName, "Email":v.email, "Jamiat":v.jamiat, "Jamaat":v.jamaat,
        "Designation":v.designation, "Zone":v.zone, "Status":v.registrationStatus, "Arrival":v.arrivalDate, "Departure":v.departureDate, "Event":v.eventName,
      })),
    });
    setLoading(false);
  };

  const handleDownload = () => {
    // Build params for backend — uses volunteers-by-event if event chosen,
    // or volunteers-by-jamiat if jamiat chosen, else all-volunteers
    const params = { sort, dir };
    if(eventId)         { downloadContextualReport({ type:"volunteers-by-event",  id:eventId, ...params, from:arrFrom, to:arrTo }); }
    else if(jamiat)     { downloadContextualReport({ type:"volunteers-by-jamiat", id:jamiat,  ...params }); }
    else if(arrFrom||arrTo) { downloadContextualReport({ type:"volunteers-by-arrival",   from:arrFrom||arrTo, to:arrTo||arrFrom, sort, dir }); }
    else if(depFrom||depTo) { downloadContextualReport({ type:"volunteers-by-departure", from:depFrom||depTo, to:depTo||depFrom, sort, dir }); }
    else                { downloadReport("volunteers"); }
    flash({type:"success",text:"Download started!"});
  };

  const inp = inpStyle(C);
  const hasAny = eventId||jamiat||jamaat||designation||zone||status||arrFrom||arrTo||depFrom||depTo;

  return (
    <div>
      <div style={{ fontSize:16, fontWeight:800, color:C.text, marginBottom:4 }}>👥 Volunteer Report</div>
      <div style={{ fontSize:12, color:C.muted, marginBottom:18 }}>Filter by any combination — leave blank to include all volunteers.</div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(180px,1fr))", gap:12, marginBottom:14 }}>
        <FilterField label="Event" C={C}>
          <select value={eventId} onChange={e=>{setEventId(e.target.value); setPreview(null);}} style={{...inp,cursor:"pointer"}}>
            <option value="">All events</option>
            {events.map(ev=><option key={ev.id} value={ev.id}>{ev.name}</option>)}
          </select>
        </FilterField>
        <FilterField label="Jamiat" C={C}>
          <input list="jamiat-options" value={jamiat} onChange={e=>{setJamiat(e.target.value); setPreview(null);}} placeholder="Any" style={inp} />
          <datalist id="jamiat-options">{jamiats.map(j=><option key={j} value={j}/>)}</datalist>
        </FilterField>
        <FilterField label="Jamaat" C={C}>
          <input value={jamaat} onChange={e=>{setJamaat(e.target.value); setPreview(null);}} placeholder="Any" style={inp} />
        </FilterField>
        <FilterField label="Designation" C={C}>
          <select value={designation} onChange={e=>{setDesig(e.target.value); setPreview(null);}} style={{...inp,cursor:"pointer"}}>
            <option value="">Any</option>
            {designations.map(d=><option key={d}>{d}</option>)}
          </select>
        </FilterField>
        <FilterField label="Zone" C={C}>
          <input value={zone} onChange={e=>{setZone(e.target.value); setPreview(null);}} placeholder="Any" style={inp} />
        </FilterField>
        <FilterField label="Registration Status" C={C}>
          <select value={status} onChange={e=>{setStatus(e.target.value); setPreview(null);}} style={{...inp,cursor:"pointer"}}>
            <option value="">Any</option>
            <option value="pending">Pending</option>
            <option value="arrived">Arrived</option>
            <option value="not_coming">Not Coming</option>
          </select>
        </FilterField>
      </div>

      <div style={{ fontSize:11, color:C.accent, fontWeight:700, textTransform:"uppercase", marginBottom:8, marginTop:6 }}>📆 Date Filters</div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(160px,1fr))", gap:12, marginBottom:14 }}>
        <FilterField label="Arriving From" C={C}><input type="date" value={arrFrom} onChange={e=>{setArrFrom(e.target.value); setPreview(null);}} style={inp} /></FilterField>
        <FilterField label="Arriving To" C={C}><input type="date" value={arrTo} onChange={e=>{setArrTo(e.target.value); setPreview(null);}} style={inp} /></FilterField>
        <FilterField label="Departing From" C={C}><input type="date" value={depFrom} onChange={e=>{setDepFrom(e.target.value); setPreview(null);}} style={inp} /></FilterField>
        <FilterField label="Departing To" C={C}><input type="date" value={depTo} onChange={e=>{setDepTo(e.target.value); setPreview(null);}} style={inp} /></FilterField>
      </div>

      <div style={{ fontSize:11, color:C.accent, fontWeight:700, textTransform:"uppercase", marginBottom:8 }}>🔃 Sort</div>
      <div style={{ display:"flex", gap:8, marginBottom:18, flexWrap:"wrap" }}>
        <select value={sort} onChange={e=>setSort(e.target.value)} style={{...inp,cursor:"pointer", flex:"0 0 200px"}}>
          {[
            ["full_name","Name"],["its_id","ITS ID"],["jamiat","Jamiat"],["jamaat","Jamaat"],
            ["designation","Designation"],["registration_status","Status"],
            ["arrival_date","Arrival Date"],["departure_date","Departure Date"],
          ].map(([k,l])=><option key={k} value={k}>Sort by {l}</option>)}
        </select>
        <button onClick={()=>setDir(d=>d==="asc"?"desc":"asc")} style={{ padding:"9px 14px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:9, color:C.accent, fontSize:12, fontWeight:700, cursor:"pointer" }}>
          {dir==="asc"?"Ascending ▲":"Descending ▼"}
        </button>
        {hasAny && <button onClick={()=>{ setEventId(""); setJamiat(""); setJamaat(""); setDesig(""); setZone(""); setStatus(""); setArrFrom(""); setArrTo(""); setDepFrom(""); setDepTo(""); setPreview(null); }} style={{ padding:"9px 12px", background:C.redSoft, border:`1px solid ${C.red}33`, borderRadius:9, color:C.red, fontSize:12, fontWeight:600, cursor:"pointer" }}>Clear All Filters</button>}
      </div>

      {preview && (<>
        <div style={{ fontSize:12, color:C.muted, marginBottom:6 }}>Preview · <strong style={{color:C.text}}>{preview.total}</strong> records match</div>
        <PreviewTable rows={preview.rows} C={C} />
      </>)}

      <ActionRow canDownload={true} onPreview={handlePreview} onDownload={handleDownload} loading={loading} C={C} />
    </div>
  );
}

// ============================================================
// EVENT REPORT BUILDER
// Filter by: status, type, date range
// ============================================================
function EventBuilder({ events, C, flash }) {
  const [status, setStatus] = useState("");
  const [type, setType]     = useState("");
  const [from, setFrom]     = useState("");
  const [to, setTo]         = useState("");
  const [sort, setSort]     = useState("name");
  const [dir, setDir]       = useState("asc");
  const [preview, setPreview] = useState(null);

  const types = useMemo(()=>[...new Set(events.map(e=>e.eventType).filter(Boolean))].sort(),[events]);

  const handlePreview = () => {
    let filtered = events.filter(e=>{
      if(status && e.status   !== status) return false;
      if(type   && e.eventType!== type)   return false;
      if(from   && e.startDate < from)   return false;
      if(to     && e.startDate > to)     return false;
      return true;
    });
    filtered.sort((a,b)=>{
      const va=String(a[sort==="start_date"?"startDate":sort==="end_date"?"endDate":sort==="event_type"?"eventType":sort]||"").toLowerCase();
      const vb=String(b[sort==="start_date"?"startDate":sort==="end_date"?"endDate":sort==="event_type"?"eventType":sort]||"").toLowerCase();
      return dir==="asc"?va.localeCompare(vb):vb.localeCompare(va);
    });
    setPreview({
      total: filtered.length,
      rows:  filtered.slice(0,8).map(e=>({ Name:e.name, Type:e.eventType, Start:e.startDate, End:e.endDate, Status:e.status })),
    });
  };

  const handleDownload = () => {
    if(status)            downloadContextualReport({ type:"events-by-status", id:status, sort, dir });
    else if(from && to)   downloadContextualReport({ type:"events-by-date", from, to, sort, dir });
    else                  downloadReport("events");
    flash({type:"success",text:"Download started!"});
  };

  const inp = inpStyle(C);
  const hasAny = status||type||from||to;

  return (
    <div>
      <div style={{ fontSize:16, fontWeight:800, color:C.text, marginBottom:4 }}>📅 Event Report</div>
      <div style={{ fontSize:12, color:C.muted, marginBottom:18 }}>Filter events by status, type or date range.</div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(180px,1fr))", gap:12, marginBottom:14 }}>
        <FilterField label="Status" C={C}>
          <select value={status} onChange={e=>{setStatus(e.target.value); setPreview(null);}} style={{...inp,cursor:"pointer"}}>
            <option value="">Any</option>
            {["Active","Draft","Completed","Cancelled"].map(s=><option key={s}>{s}</option>)}
          </select>
        </FilterField>
        <FilterField label="Event Type" C={C}>
          <select value={type} onChange={e=>{setType(e.target.value); setPreview(null);}} style={{...inp,cursor:"pointer"}}>
            <option value="">Any</option>
            {types.map(t=><option key={t}>{t}</option>)}
          </select>
        </FilterField>
        <FilterField label="Start Date From" C={C}><input type="date" value={from} onChange={e=>{setFrom(e.target.value); setPreview(null);}} style={inp} /></FilterField>
        <FilterField label="Start Date To" C={C}><input type="date" value={to} onChange={e=>{setTo(e.target.value); setPreview(null);}} style={inp} /></FilterField>
      </div>

      <div style={{ display:"flex", gap:8, marginBottom:18, flexWrap:"wrap" }}>
        <select value={sort} onChange={e=>setSort(e.target.value)} style={{...inp,cursor:"pointer", flex:"0 0 200px"}}>
          {[["name","Name"],["start_date","Start Date"],["end_date","End Date"],["status","Status"],["event_type","Type"]].map(([k,l])=><option key={k} value={k}>Sort by {l}</option>)}
        </select>
        <button onClick={()=>setDir(d=>d==="asc"?"desc":"asc")} style={{ padding:"9px 14px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:9, color:C.accent, fontSize:12, fontWeight:700, cursor:"pointer" }}>
          {dir==="asc"?"Ascending ▲":"Descending ▼"}
        </button>
        {hasAny && <button onClick={()=>{setStatus("");setType("");setFrom("");setTo("");setPreview(null);}} style={{ padding:"9px 12px", background:C.redSoft, border:`1px solid ${C.red}33`, borderRadius:9, color:C.red, fontSize:12, fontWeight:600, cursor:"pointer" }}>Clear</button>}
      </div>

      {preview && (<>
        <div style={{ fontSize:12, color:C.muted, marginBottom:6 }}>Preview · <strong style={{color:C.text}}>{preview.total}</strong> events match</div>
        <PreviewTable rows={preview.rows} C={C} />
      </>)}

      <ActionRow canDownload={true} onPreview={handlePreview} onDownload={handleDownload} C={C} />
    </div>
  );
}

// ============================================================
// ASSIGNMENT REPORT BUILDER
// Filter by: event, venue, gate, date range
// ============================================================
function AssignmentBuilder({ events, venues, C, flash }) {
  const [eventId, setEventId] = useState("");
  const [venueName, setVenue] = useState("");
  const [gateName, setGate]   = useState("");
  const [from, setFrom]       = useState("");
  const [to, setTo]           = useState("");
  const [sort, setSort]       = useState("assign_date");
  const [dir, setDir]         = useState("asc");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const handlePreview = async () => {
    setLoading(true);
    try {
      let r;
      if(eventId)        r = await API.get(`/reports/data/assignments-by-event/${eventId}`,  {params:{sort,dir}});
      else if(venueName) r = await API.get(`/reports/data/assignments-by-venue/${venueName}`,{params:{sort,dir}});
      else if(gateName)  r = await API.get(`/reports/data/assignments-by-gate/${gateName}`,  {params:{sort,dir}});
      else               r = { data:{ data:[], count:0 }};

      let rows = r.data.data || [];
      if(from) rows = rows.filter(a=>a.assign_date >= from);
      if(to)   rows = rows.filter(a=>a.assign_date <= to);

      setPreview({
        total: rows.length,
        rows:  rows.slice(0,8).map(a=>({
          Volunteer:a.volunteer_name, "ITS ID":a.its_id, Date:a.assign_date,
          Venue:a.venue_name, Gate:a.gate_name, Jamiat:a.jamiat,
        })),
      });
    } catch { flash({type:"error",text:"Preview failed."}); }
    setLoading(false);
  };

  const handleDownload = () => {
    if(eventId)        downloadContextualReport({ type:"assignments-by-event", id:eventId, sort, dir, from, to });
    else if(venueName) downloadContextualReport({ type:"assignments-by-venue", id:venueName, sort, dir });
    else if(gateName)  downloadContextualReport({ type:"assignments-by-gate",  id:gateName,  sort, dir });
    else if(from)      downloadContextualReport({ type:"assignments-by-date",  from, to:to||from, sort, dir });
    else               downloadReport("assignments");
    flash({type:"success",text:"Download started!"});
  };

  const inp = inpStyle(C);
  const canDownload = eventId||venueName||gateName||from;

  return (
    <div>
      <div style={{ fontSize:16, fontWeight:800, color:C.text, marginBottom:4 }}>🗂️ Duty Assignment Report</div>
      <div style={{ fontSize:12, color:C.muted, marginBottom:18 }}>At least one filter is required. Combine to narrow the report.</div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(180px,1fr))", gap:12, marginBottom:14 }}>
        <FilterField label="Event" C={C}>
          <select value={eventId} onChange={e=>{setEventId(e.target.value); setPreview(null);}} style={{...inp,cursor:"pointer"}}>
            <option value="">Any event</option>
            {events.map(ev=><option key={ev.id} value={ev.id}>{ev.name}</option>)}
          </select>
        </FilterField>
        <FilterField label="Venue Name" C={C}>
          <input list="venue-list" value={venueName} onChange={e=>{setVenue(e.target.value); setPreview(null);}} placeholder="Any venue" style={inp} />
          <datalist id="venue-list">{venues.map(v=><option key={v.id} value={v.name}/>)}</datalist>
        </FilterField>
        <FilterField label="Gate Name" C={C}>
          <input value={gateName} onChange={e=>{setGate(e.target.value); setPreview(null);}} placeholder="Any gate" style={inp} />
        </FilterField>
        <FilterField label="Date From" C={C}><input type="date" value={from} onChange={e=>{setFrom(e.target.value); setPreview(null);}} style={inp} /></FilterField>
        <FilterField label="Date To" C={C}><input type="date" value={to} onChange={e=>{setTo(e.target.value); setPreview(null);}} style={inp} /></FilterField>
      </div>

      <div style={{ display:"flex", gap:8, marginBottom:18, flexWrap:"wrap" }}>
        <select value={sort} onChange={e=>setSort(e.target.value)} style={{...inp,cursor:"pointer", flex:"0 0 200px"}}>
          {[["assign_date","Date"],["volunteer_name","Volunteer"],["venue_name","Venue"],["gate_name","Gate"]].map(([k,l])=><option key={k} value={k}>Sort by {l}</option>)}
        </select>
        <button onClick={()=>setDir(d=>d==="asc"?"desc":"asc")} style={{ padding:"9px 14px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:9, color:C.accent, fontSize:12, fontWeight:700, cursor:"pointer" }}>
          {dir==="asc"?"Ascending ▲":"Descending ▼"}
        </button>
        {canDownload && <button onClick={()=>{setEventId("");setVenue("");setGate("");setFrom("");setTo("");setPreview(null);}} style={{ padding:"9px 12px", background:C.redSoft, border:`1px solid ${C.red}33`, borderRadius:9, color:C.red, fontSize:12, fontWeight:600, cursor:"pointer" }}>Clear</button>}
      </div>

      {preview && (<>
        <div style={{ fontSize:12, color:C.muted, marginBottom:6 }}>Preview · <strong style={{color:C.text}}>{preview.total}</strong> assignments match</div>
        <PreviewTable rows={preview.rows} C={C} />
      </>)}

      <ActionRow canDownload={!!canDownload} onPreview={handlePreview} onDownload={handleDownload} loading={loading} C={C} />
      {!canDownload && <div style={{ fontSize:11, color:C.orange, marginTop:8 }}>⚠ Please choose an event, venue, gate or date.</div>}
    </div>
  );
}

// ============================================================
// ATTENDANCE REPORT BUILDER
// Filter by: event, status, date range
// ============================================================
function AttendanceBuilder({ events, C, flash }) {
  const [eventId, setEventId] = useState("");
  const [status, setStatus]   = useState("");
  const [from, setFrom]       = useState("");
  const [to, setTo]           = useState("");
  const [sort, setSort]       = useState("attend_date");
  const [dir, setDir]         = useState("asc");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const handlePreview = async () => {
    setLoading(true);
    try {
      let r;
      if(eventId)       r = await API.get(`/reports/data/attendance-by-event/${eventId}`, {params:{sort,dir}});
      else if(from&&to) r = await API.get(`/reports/data/attendance-by-date`,             {params:{from,to,sort,dir}});
      else              r = { data:{ data:[], count:0 }};
      let rows = r.data.data || [];
      if(status) rows = rows.filter(a=>a.status === status);
      setPreview({
        total: rows.length,
        rows:  rows.slice(0,8).map(a=>({
          Volunteer:a.volunteer_name, "ITS ID":a.its_id, Date:a.attend_date, Status:a.status, Event:a.event_name,
        })),
      });
    } catch { flash({type:"error",text:"Preview failed."}); }
    setLoading(false);
  };

  const handleDownload = () => {
    if(eventId)         downloadContextualReport({ type:"attendance-by-event", id:eventId, sort, dir });
    else if(from && to) downloadContextualReport({ type:"attendance-by-date",  from, to, sort, dir });
    else                downloadReport("attendance");
    flash({type:"success",text:"Download started!"});
  };

  const inp = inpStyle(C);
  const canDownload = eventId||(from&&to);

  return (
    <div>
      <div style={{ fontSize:16, fontWeight:800, color:C.text, marginBottom:4 }}>✅ Attendance Report</div>
      <div style={{ fontSize:12, color:C.muted, marginBottom:18 }}>Filter by event or date range. Optionally filter by status.</div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(180px,1fr))", gap:12, marginBottom:14 }}>
        <FilterField label="Event" C={C}>
          <select value={eventId} onChange={e=>{setEventId(e.target.value); setPreview(null);}} style={{...inp,cursor:"pointer"}}>
            <option value="">Any event</option>
            {events.map(ev=><option key={ev.id} value={ev.id}>{ev.name}</option>)}
          </select>
        </FilterField>
        <FilterField label="Attendance Status" C={C}>
          <select value={status} onChange={e=>{setStatus(e.target.value); setPreview(null);}} style={{...inp,cursor:"pointer"}}>
            <option value="">Any</option>
            <option value="Present">Present</option>
            <option value="Absent">Absent</option>
            <option value="Late">Late</option>
          </select>
        </FilterField>
        <FilterField label="Date From" C={C}><input type="date" value={from} onChange={e=>{setFrom(e.target.value); setPreview(null);}} style={inp} /></FilterField>
        <FilterField label="Date To" C={C}><input type="date" value={to} onChange={e=>{setTo(e.target.value); setPreview(null);}} style={inp} /></FilterField>
      </div>

      <div style={{ display:"flex", gap:8, marginBottom:18, flexWrap:"wrap" }}>
        <select value={sort} onChange={e=>setSort(e.target.value)} style={{...inp,cursor:"pointer", flex:"0 0 200px"}}>
          {[["attend_date","Date"],["volunteer_name","Volunteer"],["status","Status"]].map(([k,l])=><option key={k} value={k}>Sort by {l}</option>)}
        </select>
        <button onClick={()=>setDir(d=>d==="asc"?"desc":"asc")} style={{ padding:"9px 14px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:9, color:C.accent, fontSize:12, fontWeight:700, cursor:"pointer" }}>
          {dir==="asc"?"Ascending ▲":"Descending ▼"}
        </button>
      </div>

      {preview && (<>
        <div style={{ fontSize:12, color:C.muted, marginBottom:6 }}>Preview · <strong style={{color:C.text}}>{preview.total}</strong> records match</div>
        <PreviewTable rows={preview.rows} C={C} />
      </>)}

      <ActionRow canDownload={!!canDownload} onPreview={handlePreview} onDownload={handleDownload} loading={loading} C={C} />
      {!canDownload && <div style={{ fontSize:11, color:C.orange, marginTop:8 }}>⚠ Please choose an event or date range.</div>}
    </div>
  );
}

// ============================================================
// VENUE & GATE REPORT BUILDER
// ============================================================
function VenueBuilder({ events, C, flash }) {
  const [eventId, setEventId] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const handlePreview = async () => {
    if(!eventId) { flash({type:"error",text:"Select an event."}); return; }
    setLoading(true);
    try {
      const r = await API.get(`/reports/data/gate-fill/${eventId}`);
      const rows = (r.data.data||[]).map(g=>({
        Venue:g.venue_name, Gate:g.name, Type:g.gate_type, Min:g.min_volunteers||0, Max:g.max_volunteers||0,
        Assigned:g.assigned_count||0,
        "Fill %": g.max_volunteers ? `${Math.round((g.assigned_count||0)/g.max_volunteers*100)}%` : "—",
        Status: (g.assigned_count||0)>=(g.max_volunteers||0)&&g.max_volunteers ? "FULL" :
                (g.assigned_count||0)>=(g.min_volunteers||0)               ? "OK"   : "Understaffed",
      }));
      setPreview({ total:rows.length, rows:rows.slice(0,8) });
    } catch { flash({type:"error",text:"Preview failed."}); }
    setLoading(false);
  };

  const handleDownload = () => {
    if(eventId) downloadContextualReport({ type:"gate-fill", id:eventId });
    else        downloadReport("venues");
    flash({type:"success",text:"Download started!"});
  };

  const inp = inpStyle(C);
  return (
    <div>
      <div style={{ fontSize:16, fontWeight:800, color:C.text, marginBottom:4 }}>🏟️ Venue & Gate Report</div>
      <div style={{ fontSize:12, color:C.muted, marginBottom:18 }}>Capacity utilisation, gate fill rates and understaffed gates per event.</div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(220px,1fr))", gap:12, marginBottom:14 }}>
        <FilterField label="Event" C={C}>
          <select value={eventId} onChange={e=>{setEventId(e.target.value); setPreview(null);}} style={{...inp,cursor:"pointer"}}>
            <option value="">All venues (no fill rate)</option>
            {events.map(ev=><option key={ev.id} value={ev.id}>{ev.name}</option>)}
          </select>
        </FilterField>
      </div>

      {preview && (<>
        <div style={{ fontSize:12, color:C.muted, marginBottom:6 }}>Preview · <strong style={{color:C.text}}>{preview.total}</strong> gates · shows fill % and understaffed/full alerts</div>
        <PreviewTable rows={preview.rows} C={C} />
      </>)}

      <ActionRow canDownload={true} onPreview={handlePreview} onDownload={handleDownload} loading={loading} C={C} />
    </div>
  );
}

// ============================================================
// MESSAGING LOG REPORT BUILDER
// ============================================================
function MessagingBuilder({ C, flash }) {
  const handleDownload = () => { downloadReport("whatsapp"); flash({type:"success",text:"Download started!"}); };
  return (
    <div>
      <div style={{ fontSize:16, fontWeight:800, color:C.text, marginBottom:4 }}>💬 Messaging Log Report</div>
      <div style={{ fontSize:12, color:C.muted, marginBottom:18 }}>Full WhatsApp / SMS log — who was messaged, when, and about which duty.</div>
      <button onClick={handleDownload} style={{ padding:"10px 22px", background:C.accent, border:"none", borderRadius:10, color:"#000", fontSize:13, fontWeight:700, cursor:"pointer" }}>
        📥 Download Full Messaging Log
      </button>
    </div>
  );
}